import { Component, ViewChild } from '@angular/core';
import { FileUploader } from 'ng2-file-upload';
import { Widget, WidgetMap, WidgetMapType, WidgetSunstudy } from '../../../models/widget';
import { Auth } from '../../../auth/auth.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WidgetService } from '../../widget.service';
import { WidgetMapService } from '../../widget-map.service';
import { SunstudyService } from '../../sunstudy.service';
import { ProjectService } from '../../../projects/project.service';
import { Project } from '../../../models/project';
import { ModalComponent } from '../../../core/modal.component';
import { ActiveModal } from '../../../core/modal.service';
import { ClrWizard, ClrWizardPage } from '@clr/angular';

export enum WidgetMapBackground {
  Image = <any>"Image",
  Sunstudy = <any>"Sunstudy"
}

@Component({
  selector: 'cw-create-map-modal',
  templateUrl: './create-map-modal.component.html',
  styleUrls: ['./create-map-modal.component.scss']
})
export class CreateMapModalComponent extends ModalComponent {
  sunstudiesPromise: Promise<{}>;
  @ViewChild("wizard") wizard: ClrWizard;
  @ViewChild("finishPage") finishPage: ClrWizardPage;
  fileId: any;
  imagePreviewUrl: string;
  uploader: FileUploader = new FileUploader({});
  widget: Widget;
  map: WidgetMap;
  hasBaseDropZoneOver: boolean = false;
  form: FormGroup;
  sunstudies: WidgetSunstudy[];
  projectTree: Project;
  isSaving: boolean;
  sunstudy: WidgetSunstudy;

  constructor(private auth: Auth,
              public activeModal: ActiveModal,
              private fb: FormBuilder,
              private sunstudyService: SunstudyService,
              private projectService: ProjectService,
              private mapService: WidgetMapService,
              private widgetService: WidgetService) {

    super(activeModal);

    this.uploader.onBeforeUploadItem = (item) => {
      item.withCredentials = false;
      this.uploader.authToken = 'Bearer ' + this.auth.getToken();
    };

    this.uploader.onAfterAddingFile = f => {
      this.uploader.uploadAll();
    };

    this.uploader.onCompleteItem = (item, response) => {
      let json = JSON.parse(response);
      this.imagePreviewUrl = json.preview_url;
      this.fileId = json.filename;

      this.form.patchValue({
        name: json.title,
        type: WidgetMapType.Objects,
        width: json.dimensions.width,
        height: json.dimensions.height,
        background: {
          type: WidgetMapBackground.Image,
          fileId: `${json.filename}`,
          info: `${json.size} (${json.dimensions.width}x${json.dimensions.height})`,
          sunstudyId: null
        }
      })
    };

    this.createForm();
  }

  init(widget: Widget, map?: WidgetMap) {
    this.widget = widget;
    this.map = map;
    this.uploader.setOptions({
      url: `${widget.url}/map-upload`,
      authToken: "Bearer " + this.auth.getToken(),
    });

    this.loadSunstudies();

    if (this.map) {
      this.updateForm();
    } else {
      this.map = new WidgetMap();
    }

    this.loadProjects();
    this.opened = true;
  }

  onCommit() {
    const formValue = this.form.value;

    this.updateModel();

    if (this.map.id) {
      this.mapService.update(this.map)
        .finally(() => {
        })
        .subscribe(map => {
          this.activeModal.close(map);
        });
    } else {
      this.mapService.create(this.widget, this.map)
        .finally(() => {
        })
        .subscribe(map => {
          this.opened = false;
          // this.finishPage.completed = false;
        });
    }
  }

  fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  typeName(type: WidgetMapBackground) {
    if (type === WidgetMapBackground.Image) {
      return 'Bild';
    } else {
      return 'Solstudie';
    }
  }

  get uploadInProgress() {
    return this.uploader.isUploading;
  }

  get types() {
    return [WidgetMapBackground.Image, WidgetMapBackground.Sunstudy];
  }

  get isImage() {
    return this.form.get('background.type').value === WidgetMapBackground.Image;
  }

  get isSunstudy() {
    return this.form.get('background.type').value === WidgetMapBackground.Sunstudy;
  }

  private createForm() {
    this.form = this.fb.group({
      'type': [WidgetMapType.Objects, Validators.required],
      'name': ['', Validators.required],
      'width': [0, Validators.required],
      'height': [0, Validators.required],
      'project': ['', null],
      'background': this.fb.group({
        'fileId': [{ value: '' }, null],
        'info': [{ disabled: true, value: '' }, null],
        'type': [WidgetMapBackground.Image, Validators.required],
        'sunstudyId': ['', null],
      })
    });

    this.form.get('background.type').valueChanges.subscribe(type => {
      if (type === WidgetMapBackground.Sunstudy) {
        this.form.get('background.sunstudyId').enable();
      } else {
        this.form.get('background.sunstudyId').disable();
      }
    });

    this.form.get('background.sunstudyId').valueChanges.subscribe(sunstudyId => {
      if (sunstudyId) {
        this.sunstudiesPromise.then(() => {
          this.sunstudy = this.sunstudies.find(s => s.id === +sunstudyId);
          this.form.patchValue({
            width: this.sunstudy.width,
            height: this.sunstudy.height,
          });
        });
      }
    });

    this.form.get('project').valueChanges.subscribe(projectId => {

    });
  }

  doCancel(): void {
    this.wizard.close();
  }

  goBack(): void {
    this.wizard.previous();
  }

  private updateModel() {
    const value = this.form.value;

    this.map.type = value.type;
    this.map.name = value.name;

    if (value.background.type === WidgetMapBackground.Sunstudy) {
      this.map.sunstudy = this.getSunstudy(value.background.sunstudyId);
    } else {
      this.map.sunstudy = null;
      this.map.fileId = value.background.fileId ? value.background.fileId : null;
    }

    if (value.project) {
      this.map.project = new Project({ id: value.project });
    } else {
      this.map.project = null;
    }

    this.map.width = value.width;
    this.map.height = value.height;
  }

  private updateForm() {
    this.form.patchValue({
      type: this.map.type,
      name: this.map.name,
      width: this.map.width,
      height: this.map.height,
      project: this.map.project ? this.map.project.id : '',
      background: {
        fileId: null,
        info: null,
        type: this.map.sunstudy ? WidgetMapBackground.Sunstudy : WidgetMapBackground.Image,
        sunstudyId: this.map.sunstudy ? this.map.sunstudy.id : null
      }
    });
  }

  get projectChildren() {
    return this.projectTree.children;
  }

  private loadSunstudies() {
    if (this.sunstudies) return;

    this.sunstudiesPromise = new Promise(accept => {
      this.sunstudyService.get(this.widget).subscribe(studies => {
        this.sunstudies = studies;
        accept();
      })
    });
  }

  get isEditing() {
    return this.map && this.map.id;
  }

  private getSunstudy(id: number): WidgetSunstudy {
    return this.sunstudies.find(v => v.id === +id);
  }

  private loadProjects() {
    this.projectService.getProjectTree(this.widget.project.id).subscribe(tree => {
      this.projectTree = tree;
    })
  }

  get mapTypes() {
    return this.mapService.mapTypes;
  }
}



// WEBPACK FOOTER //
// ./src/app/widgets/widget-detail/create-map-modal/create-map-modal.component.ts